/**
 * Modal de prévisualisation des templates
 * Affiche un aperçu du template avec des données fictives
 */

import React from 'react';
import { X, FileText, FileCheck } from 'lucide-react';
import { mockInvoiceData, mockQuoteData, mockCompany } from '../utils/mockData';

// Import des templates de factures
import { InvoiceTemplateClassic } from '@/components/templates/invoice/InvoiceTemplateClassic';
import { InvoiceTemplateModern } from '@/components/templates/invoice/InvoiceTemplateModern';
import { InvoiceTemplateCorporate } from '@/components/templates/invoice/InvoiceTemplateCorporate';
import { InvoiceTemplateElegant } from '@/components/templates/invoice/InvoiceTemplateElegant';

// Import des templates de devis
import { QuoteTemplateClassic } from '@/components/templates/quote/QuoteTemplateClassic';
import { QuoteTemplateModern } from '@/components/templates/quote/QuoteTemplateModern';
import { QuoteTemplateCorporate } from '@/components/templates/quote/QuoteTemplateCorporate';
import { QuoteTemplateElegant } from '@/components/templates/quote/QuoteTemplateElegant';

interface Template {
  id: string;
  name: string;
  displayName?: string;
  type: 'INVOICE' | 'QUOTE' | 'CREDIT_NOTE' | 'WORK_ORDER' | 'PROGRESS_INVOICE';
  styleConfig: any;
}

interface TemplatePreviewModalProps {
  template: Template;
  isOpen: boolean;
  onClose: () => void;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  const templateName = template.displayName?.toLowerCase() || template.name?.toLowerCase() || 'classic';
  const primaryColor = template.styleConfig?.primaryColor || '#0d9488';
  const isInvoice = template.type === 'INVOICE';

  // Composant de prévisualisation sans les actions (fermer, imprimer, etc.)
  const PreviewWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="relative">
      {/* Masquer les boutons d'action du template */}
      <style>{`
        .template-preview .print\\:hidden,
        .template-preview [data-action-bar],
        .template-preview button:not(.preview-close) {
          display: none !important;
        }
      `}</style>
      <div className="template-preview pointer-events-none">
        {children}
      </div>
    </div>
  );

  const renderTemplate = () => {
    const commonProps = {
      onClose: () => {}, // No-op car preview seulement
      template: template as any,
      company: mockCompany as any,
      color: primaryColor,
    };

    if (isInvoice) {
      // Templates de factures
      switch (templateName) {
        case 'modern':
          return (
            <PreviewWrapper>
              <InvoiceTemplateModern invoiceData={mockInvoiceData} {...commonProps} />
            </PreviewWrapper>
          );
        case 'corporate':
          return (
            <PreviewWrapper>
              <InvoiceTemplateCorporate invoiceData={mockInvoiceData} {...commonProps} />
            </PreviewWrapper>
          );
        case 'elegant':
          return (
            <PreviewWrapper>
              <InvoiceTemplateElegant invoiceData={mockInvoiceData} {...commonProps} />
            </PreviewWrapper>
          );
        case 'classic':
        default:
          return (
            <PreviewWrapper>
              <InvoiceTemplateClassic invoiceData={mockInvoiceData} {...commonProps} />
            </PreviewWrapper>
          );
      }
    } else {
      // Templates de devis
      switch (templateName) {
        case 'modern':
          return (
            <PreviewWrapper>
              <QuoteTemplateModern quoteData={mockQuoteData} {...commonProps} />
            </PreviewWrapper>
          );
        case 'corporate':
          return (
            <PreviewWrapper>
              <QuoteTemplateCorporate quoteData={mockQuoteData} {...commonProps} />
            </PreviewWrapper>
          );
        case 'elegant':
          return (
            <PreviewWrapper>
              <QuoteTemplateElegant quoteData={mockQuoteData} {...commonProps} />
            </PreviewWrapper>
          );
        case 'classic':
        default:
          return (
            <PreviewWrapper>
              <QuoteTemplateClassic quoteData={mockQuoteData} {...commonProps} />
            </PreviewWrapper>
          );
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-xl">
              {isInvoice ? (
                <FileText className="w-5 h-5 text-teal-600" />
              ) : (
                <FileCheck className="w-5 h-5 text-teal-600" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Aperçu : {template.displayName || template.name}
              </h2>
              <p className="text-sm text-gray-500">
                {isInvoice ? 'Template de facture' : 'Template de devis'} - Données fictives
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="preview-close p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          <div className="transform scale-[0.75] origin-top">
            {renderTemplate()}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Les données affichées sont fictives et servent uniquement à la prévisualisation.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
